import _DragIndicator from "@mui/icons-material/DragIndicator";
import _Skeleton from "@mui/material/Skeleton";
import _Typography from "@mui/material/Typography";
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import { Autocomplete } from './autocomplete';
export function WayPointRow({
  defaultOptions: _defaultOptions,
  size,
  readonly,
  draggable,
  map,
  mapClickDisabled,
  mapHasContextMenu,
  geolocation,
  userPlaces,
  wayPoints,
  index,
  onBlur,
  onChange
}) {
  const [defaultOptions, setDefaultOptions] = useState();
  const [defaultValue, setDefaultValue] = useState(wayPoints[index] || null);
  const {
    t
  } = useTranslation();
  const prevWayPoints = useRef(null);
  useEffect(() => {
    const defaultOptions = [];
    if (_defaultOptions) {
      defaultOptions.push(..._defaultOptions);
    } else {
      if (geolocation) defaultOptions.push(geolocation);
      if (userPlaces) defaultOptions.push(...userPlaces);
    }
    setDefaultOptions(defaultOptions);
  }, [_defaultOptions, geolocation, userPlaces]);
  useEffect(() => {
    const wayPoint = wayPoints[index];
    if (prevWayPoints.current?.[index] !== wayPoint) setDefaultValue(wayPoint ? wayPoint.clone() : null);
    prevWayPoints.current = wayPoints;
  }, [wayPoints]);
  function handleWayPointChange(place) {
    onChange?.(index, place || undefined);
    document.getElementById(`way-point-${index}-autocomplete`)?.blur();
  }
  function handleBlur() {
    onBlur?.(index);
  }

  /**
   * A plain click on the map drops a way point, so the placeholder advertises it — unless
   * something else has taken the click over (`mapClickDisabled`), in which case the right-click
   * context menu is the shortcut left to point at, where the map has one.
   */
  function getPlaceholderKey() {
    const isDeparture = index === 0 && !wayPoints[0];
    const isArrival = wayPoints.length === 2 && index === 1 && wayPoints[0] && !wayPoints[1];
    if (isDeparture) {
      if (!mapClickDisabled) return 'commons.routing.actions.search_departure_or_click';
      return mapHasContextMenu ? 'commons.routing.actions.search_departure_or_right_click' : 'commons.routing.actions.search_departure';
    }
    if (isArrival) {
      if (!mapClickDisabled) return 'commons.routing.actions.search_arrival_or_click';
      return mapHasContextMenu ? 'commons.routing.actions.search_arrival_or_right_click' : 'commons.routing.actions.search_arrival';
    }

    // Nothing filled in yet: a plain click would go to the departure, so the arrival row only
    // ever has the context menu to offer — whether or not the click is taken over.
    if (mapHasContextMenu && wayPoints.length === 2 && index === 1 && !wayPoints[0] && !wayPoints[1]) return 'commons.routing.actions.search_arrival_or_right_click';
    return 'commons.actions.search_address';
  }
  const placeholder = t(getPlaceholderKey(), {
    context: index === 0 && !wayPoints[0] && !map ? 'no_map' : ''
  });
  return /*#__PURE__*/React.createElement(Wrapper, {
    className: [draggable ? 'shifted' : '', readonly ? 'readonly' : ''].filter(Boolean).join(' ')
  }, draggable && /*#__PURE__*/React.createElement(StyledDragIcon, {
    className: "drag-icon",
    color: "action",
    fontSize: "small"
  }), readonly ? wayPoints[index] ? /*#__PURE__*/React.createElement(StyledRow, null, /*#__PURE__*/React.createElement(_Typography, {
    noWrap: true,
    title: wayPoints[index]?.address,
    variant: "body2"
  }, wayPoints[index]?.address)) : /*#__PURE__*/React.createElement(_Typography, {
    component: "div",
    variant: "body2"
  }, /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 200
  })) : /*#__PURE__*/React.createElement(Autocomplete, {
    disableFloatingLabel: true,
    hideCustomOptionsOnSearch: true,
    center: map?.getCenter(),
    customOptions: defaultOptions,
    defaultValue: defaultValue,
    id: `way-point-${index}-autocomplete`,
    inputProps: {
      slotProps: {
        input: {
          sx: {
            '&&': {
              borderRadius: '10px',
              backgroundColor: '#fff',
              paddingX: 1.5,
              paddingY: 0.5
            }
          },
          title: placeholder
        }
      },
      onBlur: handleBlur,
      placeholder,
      label: undefined,
      margin: 'none',
      variant: 'outlined'
    },
    margin: "none",
    onSelect: handleWayPointChange,
    size: size
  }));
}
const Wrapper = styled.div`
  align-items: center;
  display: flex;
  height: 44px;
  padding-left: 24px;
  position: relative;

  &.readonly {
    height: 44px;
  }

  &.shifted {
    padding-left: 24px;
  }

  .drag-icon {
    visibility: hidden;
  }

  &:hover {
    .drag-icon {
      visibility: visible;
    }
  }
`;
const StyledDragIcon = styled(_DragIndicator)`
  left: 0px;
  position: absolute;
`;
const StyledRow = styled.div`
  align-items: center;
  display: flex;
  flex-grow: 1;
  height: 44px;
  justify-content: space-between;
  overflow-x: hidden;

  button {
    margin-left: 8px;
  }
`;